import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Users,
  ArrowLeft,
  MapPin,
  Phone,
  Mail,
  Calendar,
  Edit,
  Trash2,
  AlertCircle,
  User,
  MessageSquare,
  History,
  FileText,
  Check,
  Loader2,
  X
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS } from '../../config/api';
import PetsList from '../pets/PetsList';
import AppointmentsList from '../appointments/AppointmentsList';
import AppointmentCreateModal from '../appointments/AppointmentCreateModal';
import Breadcrumb, { BreadcrumbItem } from '../common/Breadcrumb';
import { formatPhoneNumber, unformatPhoneNumber } from '../../utils/phoneUtils';
import { useChatDock } from '../../contexts/ChatDockContext';
import { ImageUploadDropZone } from '../common/ImageUploadDropZone';
import PetOwnerAuditLog from './PetOwnerAuditLog';
import ClientTagsInput from './ClientTagsInput';

// =====================================================
// INLINE EDITABLE COMPONENTS
// =====================================================

// Inline editable contact field component
interface EditableContactFieldProps {
  value: string;
  onSave: (value: string) => Promise<void>;
  type: 'phone' | 'email';
  canEdit: boolean;
}

const EditableContactField: React.FC<EditableContactFieldProps> = ({ value, onSave, type, canEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleSave = async () => {
    let valueToSave = editValue.trim();
    if (type === 'phone' && valueToSave) {
      valueToSave = unformatPhoneNumber(valueToSave);
    }
    if (type === 'email' && valueToSave && !valueToSave.includes('@')) {
      setError('Invalid email');
      return;
    }
    if (valueToSave === value || (type === 'phone' && unformatPhoneNumber(valueToSave) === unformatPhoneNumber(value))) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    setError(null);
    try {
      await onSave(valueToSave);
      setIsEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      setIsEditing(false);
      setEditValue(value);
      setError(null);
    }
  };

  const displayValue = type === 'phone' && value ? formatPhoneNumber(value) : value;

  if (isEditing) {
    return (
      <div className="flex items-center gap-2">
        {type === 'phone' ? <Phone className="w-4 h-4 text-gray-400" /> : <Mail className="w-4 h-4 text-gray-400" />}
        <input
          ref={inputRef}
          type={type === 'email' ? 'email' : 'tel'}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => setTimeout(() => !isSaving && handleSave(), 150)}
          placeholder={type === 'phone' ? 'Enter phone...' : 'Enter email...'}
          className="px-2 py-1 text-sm border-2 border-blue-500 rounded focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
        {isSaving && <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />}
        {error && <span className="text-xs text-red-500">{error}</span>}
      </div>
    );
  }

  return (
    <div
      onClick={() => canEdit && setIsEditing(true)}
      className={`flex items-center gap-2 group ${canEdit ? 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800 rounded px-2 py-1 -mx-2 -my-1 transition-colors' : ''}`}
    >
      {type === 'phone' ? <Phone className="w-4 h-4 text-gray-400" /> : <Mail className="w-4 h-4 text-gray-400" />}
      {displayValue ? (
        <span className="text-gray-900 dark:text-gray-100">{displayValue}</span>
      ) : (
        <span className="text-gray-400 italic">{type === 'phone' ? 'Add phone...' : 'Add email...'}</span>
      )}
      {canEdit && <Edit className="w-3 h-3 text-gray-400 opacity-0 group-hover:opacity-100 transition-opacity" />}
      {justSaved && (
        <div className="bg-emerald-500 rounded-full p-0.5">
          <Check className="w-3 h-3 text-white" />
        </div>
      )}
    </div>
  );
};

// Editable notes component with yellow styling like pet special instructions
interface EditableNotesProps {
  notes: string;
  onSave: (notes: string) => Promise<void>;
  canEdit: boolean;
}

const EditableNotes: React.FC<EditableNotesProps> = ({ notes, onSave, canEdit }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(notes);
  const [isSaving, setIsSaving] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setEditValue(notes);
  }, [notes]);

  useEffect(() => {
    if (isEditing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.setSelectionRange(textareaRef.current.value.length, textareaRef.current.value.length);
    }
  }, [isEditing]);

  const handleSave = async () => {
    if (editValue.trim() === notes) {
      setIsEditing(false);
      return;
    }
    setIsSaving(true);
    try {
      await onSave(editValue.trim());
      setIsEditing(false);
      setJustSaved(true);
      setTimeout(() => setJustSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save notes:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditValue(notes);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    // Enter saves (Shift+Enter for new line)
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Only save on blur if clicking outside the entire container (not on buttons)
    if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
      handleSave();
    }
  };

  if (isEditing) {
    return (
      <div ref={containerRef} className="mt-3 bg-yellow-50 border border-yellow-200 rounded-none p-3">
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
          Notes
        </p>
        <textarea
          ref={textareaRef}
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder="Add notes... (Enter to save, Shift+Enter for new line)"
          className="w-full px-3 py-2 text-sm border border-yellow-300 rounded-none bg-white focus:ring-2 focus:ring-yellow-400 focus:border-yellow-400 resize-none"
          rows={3}
        />
        <div className="flex justify-end gap-2 mt-3">
          <button
            onClick={handleCancel}
            disabled={isSaving}
            className="px-3 py-1.5 text-sm font-medium text-gray-600 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-1"
          >
            {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
            Save
          </button>
        </div>
      </div>
    );
  }

  if (notes) {
    return (
      <div
        onClick={() => canEdit && setIsEditing(true)}
        className={`relative mt-3 bg-yellow-50 border border-yellow-200 rounded-none p-3 ${canEdit ? 'cursor-pointer hover:bg-yellow-100 transition-colors group' : ''}`}
      >
        {justSaved && (
          <div className="absolute -top-1 -right-1 bg-emerald-500 rounded-full p-0.5 animate-bounce">
            <Check className="w-3 h-3 text-white" />
          </div>
        )}
        <p className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1 flex items-center gap-2">
          Notes
          {canEdit && <Edit className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />}
        </p>
        <p className="text-sm text-gray-700 italic">"{notes}"</p>
      </div>
    );
  }

  if (!canEdit) return null;

  return (
    <button
      onClick={() => setIsEditing(true)}
      className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300 transition-colors"
    >
      <FileText className="w-4 h-4" />
      <span className="italic">Add a note...</span>
    </button>
  );
};

type TabType = 'details' | 'audit';

interface PetOwner {
  uuid: string;
  user_id?: string;  // Optional user reference
  full_name: string;
  email?: string;
  phone?: string;
  emergency_contact?: string;
  secondary_phone?: string;
  address?: string;
  preferred_communication: string;
  notifications_enabled: boolean;
  photo_url?: string;
  tags?: string[];
  notes?: string;
  created_at: string;
  updated_at: string;
}

interface CrmSummary {
  last_visit_date: string | null;
  next_appointment_date: string | null;
  touch_count: number;
  customer_status: string;
}

interface Appointment {
  id: string;
  appointment_date: string;
  status: string;
}

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

interface PetOwnerDetailProps {
  ownerIdProp?: string;  // Optional prop to override URL param (for dashboard embedding)
  inDashboard?: boolean; // Whether this is embedded in dashboard (affects navigation)
  onBookAppointment?: (pet: Pet, ownerId: string) => void; // Callback when booking appointment from pet card
}

const PetOwnerDetail: React.FC<PetOwnerDetailProps> = ({ ownerIdProp, inDashboard = false, onBookAppointment }) => {
  const { uuid: paramUuid } = useParams<{ uuid: string }>();
  const uuid = ownerIdProp || paramUuid;
  const navigate = useNavigate();
  const { user } = useAuth();
  const { startSMSToOwner } = useChatDock();

  const [petOwner, setPetOwner] = useState<PetOwner | null>(null);
  const [crmSummary, setCrmSummary] = useState<CrmSummary | null>(null);
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [isCreateAppointmentOpen, setIsCreateAppointmentOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<TabType>('details');

  const isAdmin = user?.role === 'ADMIN';

  useEffect(() => {
    if (uuid) {
      fetchPetOwner();
      fetchCrmSummary();
      fetchAppointments();
    }
  }, [uuid]);

  const fetchPetOwner = async () => {
    if (!uuid) return;

    setLoading(true);
    try {
      const response = await fetch(API_ENDPOINTS.PET_OWNERS.GET(uuid));
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error('Access denied. You can only view your own pet owner profile.');
        }
        if (response.status === 404) {
          throw new Error('Pet owner not found');
        }
        throw new Error('Failed to fetch pet owner');
      }
      const data = await response.json();
      setPetOwner(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load pet owner');
    } finally {
      setLoading(false);
    }
  };

  const fetchCrmSummary = async () => {
    if (!uuid) return;

    try {
      const response = await fetch(API_ENDPOINTS.PET_OWNER_CRM.GET(uuid));
      if (response.ok) {
        const data = await response.json();
        setCrmSummary(data);
      }
      // Silently fail if CRM data not available (not critical)
    } catch (err) {
      // Silently fail if CRM data not available
      console.warn('Failed to fetch CRM summary:', err);
    }
  };

  const fetchAppointments = async () => {
    if (!uuid) return;

    try {
      const response = await fetch(API_ENDPOINTS.APPOINTMENTS.LIST_BY_PET_OWNER(uuid));
      if (response.ok) {
        const data = await response.json();
        setAppointments(data);
      }
    } catch (err) {
      console.warn('Failed to fetch appointments:', err);
    }
  };

  // Inline update handler for phone, email, notes, tags
  const handleInlineUpdate = useCallback(async (field: string, value: any) => {
    if (!uuid || !petOwner) return;
    
    const token = localStorage.getItem('access_token') || localStorage.getItem('token');
    
    const response = await fetch(API_ENDPOINTS.PET_OWNERS.UPDATE(uuid), {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify({ [field]: value })
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `Failed to update ${field}`);
    }
    
    const updatedOwner = await response.json();
    setPetOwner(updatedOwner);
  }, [uuid, petOwner]);

  const handleAddTag = useCallback(async (tag: string) => {
    if (!petOwner) return;
    const currentTags = petOwner.tags || [];
    const newTags = [...currentTags, tag];
    await handleInlineUpdate('tags', newTags);
  }, [petOwner, handleInlineUpdate]);

  const handleRemoveTag = useCallback(async (tag: string) => {
    if (!petOwner) return;
    const currentTags = petOwner.tags || [];
    const newTags = currentTags.filter(t => t !== tag);
    await handleInlineUpdate('tags', newTags);
  }, [petOwner, handleInlineUpdate]);

  const handlePhotoUpload = async (file: File) => {
    if (!uuid) return;

    try {
      const token = localStorage.getItem('access_token') || localStorage.getItem('token');

      // 1. Initiate upload - get signed S3 URL
      const initResponse = await fetch(API_ENDPOINTS.PET_OWNER_PHOTOS.INITIATE_UPLOAD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pet_owner_id: uuid,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size
        })
      });

      if (!initResponse.ok) {
        const errorData = await initResponse.json();
        throw new Error(errorData.detail || 'Failed to initiate upload');
      }

      const { upload_url, fields, s3_key, file_id } = await initResponse.json();

      // 2. Upload to S3 using signed URL
      const formData = new FormData();
      Object.entries(fields).forEach(([key, value]) => {
        formData.append(key, value as string);
      });
      formData.append('file', file);

      const uploadResponse = await fetch(upload_url, {
        method: 'POST',
        body: formData
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3');
      }

      // 3. Complete upload - notify backend with pet_owner_id
      const completeResponse = await fetch(API_ENDPOINTS.PET_OWNER_PHOTOS.COMPLETE_UPLOAD, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          pet_owner_id: uuid,
          s3_key,
          filename: file.name,
          content_type: file.type,
          size_bytes: file.size,
          file_id
        })
      });

      if (!completeResponse.ok) {
        throw new Error('Failed to complete upload');
      }

      // 4. Update local state with new pet owner data
      const updatedOwner = await completeResponse.json();
      setPetOwner(prev => prev ? { ...prev, photo_url: updatedOwner.photo_url } : null);
    } catch (error) {
      console.error('Photo upload error:', error);
      throw error;
    }
  };

  // Helper function to format the last visit date
  const formatLastVisit = (lastVisitDate: string | null): string => {
    if (!lastVisitDate) return 'No visits';
    
    const date = new Date(lastVisitDate);
    const now = new Date();
    const diffTime = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    if (diffDays < 365) return `${Math.floor(diffDays / 30)} months ago`;
    return `${Math.floor(diffDays / 365)} years ago`;
  };

  // Helper function to calculate visit frequency
  const getVisitFrequency = (): string => {
    if (!appointments || appointments.length === 0) return 'No history';
    
    const now = new Date();
    const oneYearAgo = new Date(now);
    oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
    
    // Count completed visits in the last year
    const visitsLastYear = appointments.filter(apt => {
      const aptDate = new Date(apt.appointment_date);
      const isCompleted = ['complete', 'completed'].includes(apt.status.toLowerCase());
      const isPastYear = aptDate >= oneYearAgo && aptDate <= now;
      return isCompleted && isPastYear;
    }).length;
    
    if (visitsLastYear === 0) return 'No recent visits';
    if (visitsLastYear === 1) return '1 visit / year';
    return `${visitsLastYear} visits / year`;
  };

  const handleDelete = async () => {
    if (!petOwner || !isAdmin) return;

    if (!window.confirm('Are you sure you want to delete this pet owner? This action cannot be undone.')) {
      return;
    }

    setDeleting(true);
    try {
      const response = await fetch(API_ENDPOINTS.PET_OWNERS.DELETE(petOwner.uuid), {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error('Failed to delete pet owner');
      }

      navigate('/pet_owners');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete pet owner');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading pet owner...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto">
          <div className="bg-red-50 p-8 rounded-lg border border-red-200">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800 mb-2">Pet Owner Not Found</h2>
            <p className="text-red-600 mb-6">{error}</p>
          </div>
        </div>
      </div>
    );
  }

  if (!petOwner) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Pet Owner Not Found</h2>
          <p className="text-gray-600 mb-6">Pet owner not found</p>
        </div>
      </div>
    );
  }

  // PRACTICE_ADMIN and VET_STAFF can edit client details
  const canEdit = isAdmin || user?.role === 'PRACTICE_ADMIN' || user?.role === 'VET_STAFF';
  const canDelete = isAdmin;

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-dark-bg-primary">
      {/* Header */}
      <section className="bg-white dark:bg-dark-bg-secondary border-b border-gray-200 dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex items-center gap-6">
            <ImageUploadDropZone
              onUpload={handlePhotoUpload}
              currentImageUrl={petOwner.photo_url}
              variant="circle"
              size="lg"
            />
            <div>
              <h1 className="text-4xl font-bold text-gray-900 dark:text-dark-text-primary tracking-tight">
                {petOwner.full_name}
              </h1>
              
              {/* Contact info in a horizontal line like pet page */}
              <div className="flex items-center gap-3 mt-2 text-lg text-gray-600 dark:text-dark-text-secondary">
                {/* Phone */}
                <EditableContactField
                  value={petOwner.phone || ''}
                  onSave={async (value) => handleInlineUpdate('phone', value)}
                  type="phone"
                  canEdit={canEdit}
                />
                {petOwner.phone && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      startSMSToOwner(petOwner.uuid, petOwner.full_name, petOwner.phone!);
                    }}
                    className="p-1 text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
                    title="Send SMS"
                  >
                    <MessageSquare className="w-4 h-4" />
                  </button>
                )}
                {petOwner.phone && petOwner.email && <span className="text-gray-300">•</span>}
                {/* Email */}
                <EditableContactField
                  value={petOwner.email || ''}
                  onSave={async (value) => handleInlineUpdate('email', value)}
                  type="email"
                  canEdit={canEdit}
                />
              </div>
              
              {/* Status + Visit Info Chips + Tags on same row */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {/* Status Chip */}
                {crmSummary?.customer_status && (
                  <div className={`px-3 py-2 text-sm font-bold rounded-lg border-2 ${
                    crmSummary.customer_status === 'APPROVED' 
                      ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-800 dark:text-emerald-300 border-emerald-400'
                      : crmSummary.customer_status === 'PROSPECT'
                      ? 'bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-300 border-amber-400'
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-300 border-gray-400'
                  }`}>
                    {crmSummary.customer_status === 'APPROVED' ? 'Client' : crmSummary.customer_status.charAt(0) + crmSummary.customer_status.slice(1).toLowerCase()}
                  </div>
                )}
                
                {/* Last Visit Chip */}
                {crmSummary && crmSummary.last_visit_date && (
                  <div className="px-3 py-2 text-sm font-bold rounded-lg bg-blue-100 dark:bg-blue-500/20 text-blue-800 dark:text-blue-300 border-2 border-blue-400">
                    Last Visit: {formatLastVisit(crmSummary.last_visit_date)}
                  </div>
                )}
                
                {/* Visit Frequency Chip - only show if there are visits */}
                {appointments && appointments.length > 0 && !getVisitFrequency().includes('No') && (
                  <div className="px-3 py-2 text-sm font-bold rounded-lg border-2 bg-green-100 dark:bg-green-500/20 text-green-800 dark:text-green-300 border-green-400">
                    {getVisitFrequency()}
                  </div>
                )}
                
                {/* Tags Section - inline with visit chips */}
                <ClientTagsInput
                  tags={petOwner.tags || []}
                  onAddTag={handleAddTag}
                  onRemoveTag={handleRemoveTag}
                  canEdit={canEdit}
                />
              </div>
              
              {/* Notes Section - Yellow box like pet special instructions */}
              <div className="mt-4">
                <EditableNotes
                  notes={petOwner.notes || ''}
                  onSave={async (value) => handleInlineUpdate('notes', value)}
                  canEdit={canEdit}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="bg-white border-b border-gray-200 dark:bg-dark-bg-secondary dark:border-dark-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`flex items-center gap-2 py-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'details'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary hover:border-gray-300 dark:hover:border-dark-border'
              }`}
            >
              <User className="w-4 h-4" />
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`flex items-center gap-2 py-4 px-1 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'audit'
                  ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                  : 'border-transparent text-gray-500 dark:text-dark-text-muted hover:text-gray-700 dark:hover:text-dark-text-secondary hover:border-gray-300 dark:hover:border-dark-border'
              }`}
            >
              <History className="w-4 h-4" />
              Audit
            </button>
          </nav>
        </div>
      </div>

      {/* Content */}
      {activeTab === 'audit' ? (
        <section className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 dark:bg-dark-bg-secondary dark:border-dark-border">
              <PetOwnerAuditLog ownerId={uuid} />
            </div>
          </div>
        </section>
      ) : (
      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Pets Section - Most Important */}
          <div className="mb-8">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <PetsList
                ownerUuid={petOwner.uuid}
                petOwner={{
                  id: petOwner.uuid,
                  user_id: petOwner.user_id,
                  full_name: petOwner.full_name
                }}
                onBookAppointment={onBookAppointment ? (pet) => onBookAppointment(pet, petOwner.uuid) : undefined}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Contact Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <Phone className="w-5 h-5 mr-2 text-gray-600" />
                Contact Information
              </h2>

              <div className="space-y-4">
                {petOwner.emergency_contact && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Emergency Contact
                    </label>
                    <div className="flex items-center text-gray-900">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      {formatPhoneNumber(petOwner.emergency_contact)}
                    </div>
                  </div>
                )}

                {petOwner.secondary_phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Secondary Phone
                    </label>
                    <div className="flex items-center text-gray-900">
                      <Phone className="w-4 h-4 mr-2 text-gray-400" />
                      {formatPhoneNumber(petOwner.secondary_phone)}
                    </div>
                  </div>
                )}

                {petOwner.address && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Address
                    </label>
                    <div className="flex items-start text-gray-900">
                      <MapPin className="w-4 h-4 mr-2 text-gray-400 mt-1 flex-shrink-0" />
                      <span>{petOwner.address}</span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Preferences */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <Mail className="w-5 h-5 mr-2 text-gray-600" />
                Preferences
              </h2>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Preferred Communication
                  </label>
                  <div className="flex items-center text-gray-900">
                    <Mail className="w-4 h-4 mr-2 text-gray-400" />
                    <span className="capitalize">{petOwner.preferred_communication}</span>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notifications
                  </label>
                  <div className="flex items-center text-gray-900">
                    <div className={`w-3 h-3 rounded-full mr-2 ${petOwner.notifications_enabled ? 'bg-green-500' : 'bg-red-500'
                      }`} />
                    <span>{petOwner.notifications_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>
              </div>
            </div>
            {/* Scheduled Appointments Section */}
            <div className="mt-8">
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
                <AppointmentsList
                  petOwnerId={petOwner.uuid}
                  showHeader={true}
                  maxItems={5}
                  showCreateButton={true}
                  onScheduleAppointment={() => setIsCreateAppointmentOpen(true)}
                />
              </div>
            </div>



            {/* System Information */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 lg:col-span-2">
              <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-gray-600" />
                System Information
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    User ID
                  </label>
                  <p className="text-gray-900 font-mono text-sm">{petOwner.user_id}</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Created
                  </label>
                  <p className="text-gray-900">
                    {new Date(petOwner.created_at).toLocaleDateString()}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Updated
                  </label>
                  <p className="text-gray-900">
                    {new Date(petOwner.updated_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
          </div>


        </div>
      </section>
      )}

      {/* Create Appointment Modal */}
      <AppointmentCreateModal
        isOpen={isCreateAppointmentOpen}
        onClose={() => setIsCreateAppointmentOpen(false)}
        onSuccess={() => {
          setIsCreateAppointmentOpen(false);
          fetchAppointments(); // Refresh appointments list
        }}
        initialPetOwnerId={petOwner.uuid}
      />
    </div>
  );
};

export default PetOwnerDetail;
